import { useState } from "react";
import {
  useWebhooks,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
  useTestWebhook,
} from "@/hooks/useWebhooks";
import { WebhookDeliveryLog } from "./WebhookDeliveryLog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Loader2,
  Trash2,
  Plus,
  Play,
  History,
  Lock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AVAILABLE_EVENTS = [
  { id: "monitor.down", label: "Monitor Down" },
  { id: "monitor.recovery", label: "Monitor Recovery" },
  { id: "ssl.expiry", label: "SSL Expiry" },
  { id: "domain.expiry", label: "Domain Expiry" },
  { id: "incident.created", label: "Incident Created" },
  { id: "incident.resolved", label: "Incident Resolved" },
  { id: "maintenance.started", label: "Maintenance Started" },
  { id: "maintenance.ended", label: "Maintenance Ended" },
];

export function WebhookConfig() {
  const { data: webhooks = [], isLoading } = useWebhooks();
  const createMutation = useCreateWebhook();
  const updateMutation = useUpdateWebhook();
  const deleteMutation = useDeleteWebhook();
  const testMutation = useTestWebhook();

  // Webhook form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secretRaw, setSecretRaw] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>(["monitor.down", "monitor.recovery"]);
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);

  // Deliveries log open states
  const [openDeliveryLogId, setOpenDeliveryLogId] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !url) return toast.error("Name and URL are required.");
    try {
      await createMutation.mutateAsync({
        name,
        url,
        secret_raw: secretRaw || null,
        events: selectedEvents,
        enabled: true,
      });
      toast.success("Webhook integration created");
      // Reset form
      setName("");
      setUrl("");
      setSecretRaw("");
      setSelectedEvents(["monitor.down", "monitor.recovery"]);
      setShowAddForm(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to create webhook");
    }
  };

  const handleStatusToggle = async (id: string, currentlyEnabled: boolean) => {
    try {
      await updateMutation.mutateAsync({
        id,
        webhook: { enabled: !currentlyEnabled },
      });
      toast.success(currentlyEnabled ? "Webhook disabled" : "Webhook enabled");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success("Webhook deleted");
      if (openDeliveryLogId === id) setOpenDeliveryLogId(null);
    } catch {
      toast.error("Failed to delete webhook");
    }
  };

  const handleTest = async (id: string) => {
    setTestingWebhookId(id);
    try {
      const res = await testMutation.mutateAsync(id);
      if (res && res.response_code >= 200 && res.response_code < 300) {
        toast.success(`Webhook test passed (HTTP ${res.response_code})`);
      } else {
        toast.error(`Webhook test failed: HTTP ${res?.response_code || 'Error'} — ${res?.error_message || 'Connection refused'}`);
      }
    } catch (err: any) {
      toast.error(err.message || "Webhook test request failed");
    } finally {
      setTestingWebhookId(null);
    }
  };

  const toggleEventSelection = (eventId: string) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId]
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-black uppercase tracking-tight">Generic Webhooks</p>
          <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">
            Send HTTP POST payloads to custom URLs on events
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="h-8 border-2 border-foreground bg-primary hover:bg-foreground hover:text-background text-[10px] font-black uppercase shadow-brutal-sm rounded-none"
        >
          {showAddForm ? "Cancel" : <><Plus className="h-3.5 w-3.5 mr-1" /> Add Webhook</>}
        </Button>
      </div>

      {/* Add Webhook Form */}
      {showAddForm && (
        <form onSubmit={handleCreate} className="border-2 border-foreground bg-foreground/[0.02] p-5 space-y-4 rounded-none">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase opacity-60">Webhook Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Discord Webhook endpoint, Slack proxy, Slack automation, etc."
                className="h-10 rounded-none border-2 border-foreground text-[10px] focus-visible:ring-primary shadow-brutal-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase opacity-60">Destination URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.yourdomain.com/v1/uptime-webhook"
                className="h-10 rounded-none border-2 border-foreground text-[10px] focus-visible:ring-primary shadow-brutal-sm"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] font-black uppercase opacity-60 flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Secret Signing Key (Optional)
            </Label>
            <Input
              value={secretRaw}
              onChange={(e) => setSecretRaw(e.target.value)}
              placeholder="Enables HMAC-SHA256 signatures in the headers (x-uptimewing-signature)"
              className="h-10 rounded-none border-2 border-foreground text-[10px] focus-visible:ring-primary shadow-brutal-sm"
            />
          </div>

          {/* Event Triggers Checklist */}
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase opacity-60">Event Triggers</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {AVAILABLE_EVENTS.map((evt) => {
                const isSelected = selectedEvents.includes(evt.id);
                return (
                  <button
                    type="button"
                    key={evt.id}
                    onClick={() => toggleEventSelection(evt.id)}
                    className={`h-8 border-2 text-[8px] font-black uppercase text-left px-3 select-none flex items-center justify-between rounded-none shadow-brutal-xs hover:border-foreground transition-colors ${
                      isSelected
                        ? "bg-primary text-primary-foreground border-foreground"
                        : "bg-background text-muted-foreground border-border"
                    }`}
                  >
                    <span>{evt.label}</span>
                    {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-foreground" />}
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            type="submit"
            disabled={createMutation.isPending}
            className="h-10 border-2 border-foreground bg-foreground text-background font-black uppercase text-[10px] shadow-brutal-sm rounded-none hover:bg-primary transition-all"
          >
            {createMutation.isPending ? "Creating..." : "Save Webhook"}
          </Button>
        </form>
      )}

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-border text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
          No generic webhooks configured yet.
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks.map((w) => {
            const isLogOpen = openDeliveryLogId === w.id;
            return (
              <div key={w.id} className="border-2 border-foreground bg-background rounded-none">
                <div className="flex flex-wrap items-center justify-between p-4 gap-4">
                  <div className="space-y-1">
                    <span className="text-xs font-black uppercase tracking-tight">{w.name}</span>
                    <p className="text-[10px] font-mono text-muted-foreground break-all">{w.url}</p>
                    <div className="flex flex-wrap gap-1.5 pt-1.5">
                      {w.events && w.events.length > 0 ? (
                        w.events.map((evt: string) => (
                          <span key={evt} className="px-1.5 py-0.5 border border-foreground/10 text-[7px] font-bold uppercase bg-muted/20">
                            {evt}
                          </span>
                        ))
                      ) : (
                        <span className="px-1.5 py-0.5 border border-foreground/10 text-[7px] font-bold uppercase bg-muted/20 text-muted-foreground">
                          All Events
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Enable toggle */}
                    <div className="flex items-center gap-2 border-r-2 border-foreground/15 pr-3">
                      <span className="text-[8px] font-black uppercase text-muted-foreground">Active</span>
                      <Switch
                        checked={w.enabled}
                        onCheckedChange={() => handleStatusToggle(w.id, w.enabled)}
                      />
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => handleTest(w.id)}
                      disabled={testingWebhookId === w.id}
                      className="h-8 border-2 border-foreground text-[9px] font-black uppercase shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
                    >
                      {testingWebhookId === w.id ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <Play className="h-3 w-3 mr-1" />
                      )}
                      Test
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => setOpenDeliveryLogId(isLogOpen ? null : w.id)}
                      className={`h-8 border-2 border-foreground text-[9px] font-black uppercase shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none ${
                        isLogOpen ? "bg-muted/30" : ""
                      }`}
                    >
                      <History className="h-3 w-3 mr-1" />
                      Logs
                      {isLogOpen ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                    </Button>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 text-[9px] font-black uppercase text-status-down hover:bg-status-down/10"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="glass-strong rounded-none border-4 border-foreground bg-background shadow-brutal p-6">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-base font-black uppercase tracking-tight">Delete Webhook</AlertDialogTitle>
                          <AlertDialogDescription className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-2">
                            Are you sure? This will delete this webhook integration and its logs permanently.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="mt-4 gap-2 flex-row justify-end">
                          <AlertDialogCancel className="rounded-none border-2 border-foreground bg-background text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all mt-0">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(w.id)}
                            className="rounded-none border-2 border-foreground bg-status-down text-white hover:bg-foreground hover:text-background font-black uppercase text-[10px] shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {/* Collapsible Delivery Logs */}
                {isLogOpen && (
                  <div className="p-4 border-t-2 border-foreground bg-foreground/[0.01]">
                    <WebhookDeliveryLog webhookId={w.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
